<script setup>
import { ref, computed, watch } from 'vue'
import { Head, router, useForm } from '@inertiajs/vue3'
import {
    PhNotePencil , PhPencilSimple, PhTrash, PhCaretRight, PhCheck, PhBook, PhBank,
} from '@phosphor-icons/vue'

import Layout      from '@pages/Shared/layouts/AdminLayout.vue'
import Modal       from '@pages/Shared/Modal.vue'
import Prompt      from '@pages/Shared/Prompt.vue'
import Select      from '@pages/Shared/form/Select.vue'
import MultiSelect from '@pages/Shared/form/MultiSelect.vue'
import Input       from '@pages/Shared/form/Input.vue'
import Table       from '@pages/Shared/ui/Table.vue'
import Spinner     from '@pages/Shared/ui/Spinner.vue'
import { flashToast } from '@/toast.js'
import { capitalize } from '@/helpers.js'

/* -------------------- PROPS -------------------- */
const props = defineProps({
    assessments:  Array,
    sessions:     Array,
    terms:        Array,
    classes:      Array,
    sessionTerms: Array,
    types:        Array,
    filters:      Object,
})

/* -------------------- TABLE COLUMNS -------------------- */
const columns = [
    { key: 'index',   label: '#',          type: 'index' },
    { key: 'name',    label: 'Assessment', type: 'slot'  },
    { key: 'session', label: 'Session',    type: 'slot'  },
    { key: 'term',    label: 'Term',       type: 'slot'  },
    { key: 'type',    label: 'Type',       type: 'slot'  },
    { key: 'weight',  label: 'Weight',     type: 'slot'  },
    { key: 'classes', label: 'Classes',    type: 'slot'  },
    { key: 'status',  label: 'Status',     type: 'slot'  },
    { key: 'actions', label: '',           type: 'slot'  },
]

/* -------------------- FILTERS -------------------- */
const filters = ref({
    search:  props.filters?.search  ?? null,
    session: props.filters?.session ?? null,
    term:    props.filters?.term    ?? null,
    type:    props.filters?.type    ?? null,   
})

watch(() => props.filters, (val) => { filters.value = { ...val } }, { deep: true })

const termFilterOptions = computed(() => {
    const unique = [...new Map(props.terms.map(t => [t.name, t])).values()]
    return [
        { label: 'All Terms', value: null },
        ...unique.map(t => ({ label: t.name, value: t.name })),
    ]
})

const applyFilters = () => {
    const cleaned = Object.fromEntries(
        Object.entries(filters.value)
            .filter(([_, v]) => v !== null && v !== undefined && v !== '')
    )
    router.get('/admin/assessments', { ...cleaned, filter: 1 }, {
        preserveState: true, preserveScroll: true, replace: true,
    })
}

const handleSearch = (value)      => { filters.value.search = value; applyFilters() }
const handleFilter = (newFilters) => { filters.value = { ...filters.value, ...newFilters }; applyFilters() }

/* -------------------- EXPANDED ROWS -------------------- */
const expandedId   = ref(null)
const toggleExpand = (id) => { expandedId.value = expandedId.value === id ? null : id }

/* -------------------- FORM OPTIONS -------------------- */
// typeOptions — show "Continuous Assessment (CA)"
const typeOptions = computed(() =>
    props.types.map(t => ({ label: `${t.name} (${t.code})`, value: t.id }))
)

const classOptions = computed(() =>
    props.classes.map(c => ({ label: c.name, value: c.id }))
)

const sessionTermOptions = computed(() => {
    const map = new Map()
    props.sessionTerms.forEach(st => map.set(st.value, { label: st.label, value: st.value }))

    if (isEditing.value && editingAssessment.value) {
        const key = `${editingAssessment.value.academic_session.id}|${editingAssessment.value.academic_term.id}`
        if (!map.has(key)) {
            map.set(key, {
                label: `${editingAssessment.value.academic_session.name} - ${editingAssessment.value.academic_term.name} (inactive)`,
                value: key,
            })
        }
    }

    return Array.from(map.values())
})

/* -------------------- MODAL -------------------- */
const openModal          = ref(false)
const isEditing          = ref(false)
const editingAssessment  = ref(null)
const showDeletePrompt   = ref(false)
const assessmentToDelete = ref(null)

const form = useForm({
    session_term_id:     null,
    academic_session_id: null,
    academic_term_id:    null,
    name:                '',
    type_id:             null,
    weight:              1,
    class_room_ids:      [],
    is_active:           true,
})

const handleModalClose = () => {
    form.reset()
    form.clearErrors()
    isEditing.value       = false
    editingAssessment.value = null
    openModal.value       = false
}

const openAddModal = () => {
    form.clearErrors()
    openModal.value = true
}

const handleEdit = (assessment) => {
    isEditing.value       = true
    editingAssessment.value = assessment
    form.clearErrors()

    form.name            = assessment.name
    form.type_id         = assessment.type?.id ?? null
    form.weight          = Number(assessment.weight)
    form.session_term_id = `${assessment.academic_session.id}|${assessment.academic_term.id}`
    form.class_room_ids  = assessment.classes.map(c => c.id)
    form.is_active       = assessment.is_active

    openModal.value = true
}

const handleSubmit = () => {
    if (!form.session_term_id) {
        return flashToast({ type: 'error', message: 'Please select session and term' })
    }

    const [sessionId, termId] = form.session_term_id.split('|')
    form.academic_session_id  = sessionId
    form.academic_term_id     = termId

    const duplicate = props.assessments.some(a =>
        a.name.toLowerCase() === form.name.toLowerCase() &&
        a.academic_session?.id == sessionId &&
        a.academic_term?.id == termId &&
        (!isEditing.value || a.id !== editingAssessment.value.id)
    )

    if (duplicate) {
        return flashToast({
            type:    'error',
            message: 'An assessment with this name already exists for the selected session and term.',
        })
    }

    const url    = isEditing.value ? `/admin/assessments/${editingAssessment.value.id}` : '/admin/assessments'
    const method = isEditing.value ? 'put' : 'post'

    form.submit(method, url, {
        onSuccess: (page) => { handleModalClose(); flashToast(page.props.flash) },
        onError:   ()     => flashToast({ type: 'error', message: 'Check form for errors' }),
    })
}

const confirmDelete = (assessment) => {
    assessmentToDelete.value = assessment
    showDeletePrompt.value   = true
}

const handleDelete = () => {
    router.delete(`/admin/assessments/${assessmentToDelete.value.id}`, {
        onSuccess: (page) => {
            showDeletePrompt.value   = false
            assessmentToDelete.value = null
            flashToast(page.props.flash)
        },
        onError: () => flashToast({ type: 'error', message: 'Failed to delete assessment' }),
    })
}

const getTotalSubjects = (classes) =>
    classes.reduce((sum, cls) => sum + (cls.subjects?.length || 0), 0)
</script>

<template>
    <Layout>
        <Head title="Assessments" />

        <!-- Header -->
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Assessments</h2>
                <p class="text-sm text-gray-500 mt-0.5">Manage CA and exam definitions per term</p>
            </div>
            <button @click="openAddModal"
                class="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 text-sm font-semibold
                       text-white bg-primary hover:bg-primary-light rounded-lg transition">
                <PhNotePencil :size="18" /> Create Assessment
            </button>
        </div>

        <!-- Table -->
        <Table
            :data="{ data: assessments, links: [] }"
            :columns="columns"
            row-key="id"
            :expanded-id="expandedId"
            :actions="{ view: false, edit: false, delete: false }"
            :filters="[
                {
                    key:     'session',
                    label:   'Session',
                    options: [{ label: 'All Sessions', value: null }, ...sessions.map(s => ({ label: s.name, value: s.id }))],
                    value:   filters.session,
                },
                {
                    key:     'term',
                    label:   'Term',
                    options: termFilterOptions,
                    value:   filters.term,
                },
                {
                    key:     'type',
                    label:   'Type',
                    options: [{ label: 'All Types', value: null }, ...types.map(t => ({ label: `${t.name} (${t.code})`, value: t.id }))],
                    value:   filters.type,
                },
            ]"
            @search="handleSearch"
            @filter="handleFilter"
        >
            <template #name="{ row }">
                <button class="flex items-center gap-1.5 font-semibold text-primary hover:text-primary-light transition"
                    @click="toggleExpand(row.id)">
                    <PhCaretRight :size="14" class="transition-transform"
                        :class="expandedId === row.id ? 'rotate-90' : ''" />
                    {{ row.name }}
                </button>
            </template>

            <template #session="{ row }">
                <span class="text-sm text-gray-700">{{ row.academic_session?.name ?? '—' }}</span>
            </template>

            <template #term="{ row }">
                <span class="text-sm text-gray-700">{{ row.academic_term?.name ?? '—' }}</span>
            </template>

            <template #type="{ row }">
                <span class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md border"
                    :class="row.type?.code === 'CA'
                        ? 'bg-purple-50 text-purple-600 border-purple-100'
                        : 'bg-indigo-50 text-indigo-600 border-indigo-100'">
                    {{ row.type?.code ?? '—' }}
                </span>
            </template>

            <template #weight="{ row }">
                <span class="inline-flex items-center px-2 py-0.5 text-xs font-medium
                             bg-gray-50 text-gray-600 rounded-md border border-gray-200">
                    {{ row.weight }}%
                </span>
            </template>

            <template #classes="{ row }">
                <button v-if="row.classes.length"
                    class="flex items-center gap-1.5 text-sm text-gray-600 hover:text-primary transition"
                    @click="toggleExpand(row.id)">
                    <PhBank :size="14" class="text-gray-400" />
                    <span>{{ row.classes.length }} class{{ row.classes.length !== 1 ? 'es' : '' }}</span>
                    <span class="text-gray-300">·</span>
                    <PhBook :size="14" class="text-gray-400" />
                    <span>{{ getTotalSubjects(row.classes) }} subject{{ getTotalSubjects(row.classes) !== 1 ? 's' : '' }}</span>
                </button>
                <span v-else class="text-xs text-gray-400 italic">No classes</span>
            </template>

            <template #status="{ row }">
                <span class="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium rounded-full border"
                    :class="row.is_active
                        ? 'bg-green-50 text-green-700 border-green-100'
                        : 'bg-gray-50 text-gray-500 border-gray-200'">
                    <span class="w-1.5 h-1.5 rounded-full"
                        :class="row.is_active ? 'bg-green-500' : 'bg-gray-400'"></span>
                    {{ row.is_active ? 'Active' : 'Inactive' }}
                </span>
            </template>

            <template #actions="{ row }">
                <div class="flex items-center justify-end gap-2">
                    <button @click="handleEdit(row)"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium
                               text-green-600 bg-green-50 hover:bg-green-100 rounded-md border border-green-100 transition">
                        <PhPencilSimple :size="13" /> Edit
                    </button>
                    <button @click="confirmDelete(row)"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium
                               text-red-600 bg-red-50 hover:bg-red-100 rounded-md border border-red-100 transition">
                        <PhTrash :size="13" /> Delete
                    </button>
                </div>
            </template>

            <template #expanded="{ row }">
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div v-for="cls in row.classes" :key="cls.id"
                        class="bg-white border border-gray-200 rounded-lg p-3">
                        <div class="flex items-center gap-2 mb-2">
                            <PhBank :size="14" class="text-primary shrink-0" />
                            <span class="text-sm font-semibold text-gray-800">{{ capitalize(cls.name) }}</span>
                        </div>
                        <div v-if="cls.subjects?.length" class="flex flex-wrap gap-1.5">
                            <span v-for="subject in cls.subjects" :key="subject.id"
                                class="inline-flex items-center gap-1 px-2 py-0.5 text-xs
                                       bg-primary-50 text-primary rounded-md border border-primary-100">
                                <PhCheck :size="10" class="text-edit" />
                                {{ subject.name }}
                            </span>
                        </div>
                        <p v-else class="text-xs text-gray-400 italic">No subjects assigned</p>
                    </div>
                </div>
            </template>
        </Table>

        <!-- Modal -->
        <Modal
            v-model="openModal"
            :title="isEditing ? 'Edit Assessment' : 'Create Assessment'"
            size="medium"
            @close="handleModalClose"
        >
            <form @submit.prevent="handleSubmit" class="p-6 space-y-5">
                <Select
                    v-model="form.session_term_id"
                    :options="sessionTermOptions"
                    label="Session / Term"
                    placeholder="Select session and term"
                    :error="form.errors.academic_session_id || form.errors.academic_term_id"
                />
                <MultiSelect
                    v-model="form.class_room_ids"
                    :options="classOptions"
                    label="Classes"
                    placeholder="Select classes"
                    :error="form.errors.class_room_ids"
                />
                <Input
                    v-model="form.name"
                    label="Assessment Name"
                    placeholder="e.g., First CA, Mid-Term Exam"
                    :error="form.errors.name"
                />
                <Select
                    v-model="form.type_id"
                    :options="typeOptions"
                    label="Assessment Type"
                    placeholder="Select assessment type"
                    :error="form.errors.type_id"
                />
                <Input
                    type="number"
                    v-model="form.weight"
                    label="Weight (%)"
                    placeholder="20"
                    min="1"
                    max="100"
                    :error="form.errors.weight"
                />

                <!-- Active toggle -->
                <div class="flex items-center justify-between py-2 border-t border-gray-100">
                    <div>
                        <label class="text-sm font-medium text-gray-700">Active Status</label>
                        <p class="text-xs text-gray-500 mt-0.5">Only active assessments can receive scores</p>
                    </div>
                    <button type="button" @click="form.is_active = !form.is_active"
                        class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                        :class="form.is_active ? 'bg-edit' : 'bg-gray-300'">
                        <span class="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
                            :class="form.is_active ? 'translate-x-6' : 'translate-x-1'" />
                    </button>
                </div>

                <div class="flex flex-col gap-3 pt-4 border-t border-gray-100 sm:flex-row">
                    <button type="submit" :disabled="form.processing"
                        class="w-full px-5 py-2.5 text-sm font-medium text-white rounded-lg transition
                               disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
                        :class="isEditing ? 'bg-edit hover:bg-edit-light' : 'bg-primary hover:bg-primary-light'">
                        <Spinner v-if="form.processing" :size="14" />
                        {{ isEditing ? 'Update Assessment' : 'Create Assessment' }}
                    </button>
                </div>
            </form>
        </Modal>

        <!-- Delete Prompt -->
        <Prompt
            :isShow="showDeletePrompt"
            title="Delete Assessment"
            :message="`Are you sure you want to delete '${assessmentToDelete?.name}'? This cannot be undone.`"
            confirmText="Delete"
            @delete="handleDelete"
            @cancel="() => { showDeletePrompt = false; assessmentToDelete = null }"
        />
    </Layout>
</template>